import axios from "axios";
import { API_BASE_URL } from "./api";
import {
  clearLegacySessionTokens,
  SUPPORT_SESSION_POLICY,
} from "./sessionPolicy";

const SUPPORT_USER_STORAGE_KEY = SUPPORT_SESSION_POLICY.snapshotStorageKey;
const SUPPORT_SESSION_HINT_KEY = SUPPORT_SESSION_POLICY.sessionHintKey;
const SUPPORT_SESSION_EVENT = SUPPORT_SESSION_POLICY.sessionEvent;

const supportApi = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

const canUseLocalStorage = () =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const readJsonFromStorage = (key) => {
  if (!canUseLocalStorage()) return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeJsonToStorage = (key, value) => {
  if (!canUseLocalStorage()) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage write errors.
  }
};

const removeStorageKey = (key) => {
  if (!canUseLocalStorage()) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore storage removal errors.
  }
};

const normalizeSupportUser = (supportUser) => {
  if (!supportUser || typeof supportUser !== "object") return null;
  const id = String(supportUser.id || supportUser._id || "").trim();
  if (!id) return null;
  return {
    id,
    name: String(supportUser.name || "").trim(),
    email: String(supportUser.email || "").trim(),
    role: String(supportUser.role || "").trim(),
    roleId: supportUser.roleId || null,
    roleLabel: String(supportUser.roleLabel || "").trim(),
    permissions: Array.isArray(supportUser.permissions) ? supportUser.permissions : [],
    team: String(supportUser.team || "general").trim() || "general",
    departmentId: supportUser.departmentId || null,
    functions: Array.isArray(supportUser.functions) ? supportUser.functions : [],
    assignmentNotes: String(supportUser.assignmentNotes || ""),
    availability: String(supportUser.availability || "available"),
    isSuperAdmin: Boolean(supportUser.isSuperAdmin),
    mustChangePassword: Boolean(supportUser.mustChangePassword),
    source: String(supportUser.source || "support"),
  };
};

const notifySupportSessionChanged = (reason) => {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(
      new CustomEvent(SUPPORT_SESSION_EVENT, {
        detail: {
          reason,
          supportUser: readJsonFromStorage(SUPPORT_USER_STORAGE_KEY),
          hasSession: Boolean(
            window.localStorage.getItem(SUPPORT_SESSION_HINT_KEY) === "1"
          ),
        },
      })
    );
  } catch {
    // Ignore CustomEvent failures.
  }
};

const clearSessionCookies = async () => {
  try {
    await fetch(`${API_BASE_URL}${SUPPORT_SESSION_POLICY.logoutPath}`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    // Best-effort cookie cleanup.
  }
};

export const getStoredSupportUser = () => readJsonFromStorage(SUPPORT_USER_STORAGE_KEY);

export const hasSupportSessionHint = () => {
  if (!canUseLocalStorage()) return false;
  return (
    window.localStorage.getItem(SUPPORT_SESSION_HINT_KEY) === "1" ||
    Boolean(getStoredSupportUser()?.id)
  );
};

export const setStoredSupportUser = (supportUser, options = {}) => {
  const snapshot = normalizeSupportUser(supportUser);
  if (snapshot) {
    writeJsonToStorage(SUPPORT_USER_STORAGE_KEY, snapshot);
    if (canUseLocalStorage()) {
      try {
        window.localStorage.setItem(SUPPORT_SESSION_HINT_KEY, "1");
      } catch {
        // Ignore storage write errors.
      }
    }
  } else {
    removeStorageKey(SUPPORT_USER_STORAGE_KEY);
    removeStorageKey(SUPPORT_SESSION_HINT_KEY);
  }
  if (!options.silent) {
    notifySupportSessionChanged(options.reason || (snapshot ? "set" : "clear"));
  }
  return snapshot;
};

export const clearSupportSession = async (options = {}) => {
  if (options.server !== false) {
    await clearSessionCookies();
  }
  clearLegacySessionTokens();
  removeStorageKey(SUPPORT_USER_STORAGE_KEY);
  removeStorageKey(SUPPORT_SESSION_HINT_KEY);
  notifySupportSessionChanged(options.reason || "logout");
};

const resolveLoginPath = (path) => {
  if (path.startsWith("/u/admin") || path.startsWith("/u/super")) {
    return "/u/admin/login";
  }
  if (path.startsWith("/u/support/admin")) {
    return "/u/support/admin/login";
  }
  if (path.startsWith("/u/support/portal")) {
    return "/u/support/agent/login";
  }
  if (path.startsWith("/u/support/agent")) {
    return "/u/support/agent/login";
  }
  return "/u/support/login";
};

supportApi.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if ((status === 401 || status === 403) && typeof window !== "undefined") {
      if (hasSupportSessionHint()) {
        void clearSupportSession({ server: false, reason: "expired" });
      }
      const path = window.location?.pathname || "";
      const loginPath = resolveLoginPath(path);
      if (!path.startsWith(loginPath)) {
        window.location.assign(loginPath);
      }
    }
    return Promise.reject(error);
  }
);

export const getSupportSession = async () => {
  const res = await supportApi.get(SUPPORT_SESSION_POLICY.bootstrapPath);
  const supportUser = res.data?.supportUser || res.data || null;
  setStoredSupportUser(supportUser, { reason: "bootstrap" });
  return supportUser;
};

export const subscribeToSupportSession = (handler) => {
  if (typeof window === "undefined") return () => {};
  const safeHandler = (detail) => {
    if (typeof handler !== "function") return;
    handler(
      detail || {
        supportUser: getStoredSupportUser(),
        hasSession: hasSupportSessionHint(),
      }
    );
  };

  const handleCustomEvent = (event) => safeHandler(event.detail);
  const handleStorageEvent = (event) => {
    if (![SUPPORT_USER_STORAGE_KEY, SUPPORT_SESSION_HINT_KEY].includes(event.key)) return;
    safeHandler({
      supportUser: getStoredSupportUser(),
      hasSession: hasSupportSessionHint(),
    });
  };

  window.addEventListener(SUPPORT_SESSION_EVENT, handleCustomEvent);
  window.addEventListener("storage", handleStorageEvent);
  return () => {
    window.removeEventListener(SUPPORT_SESSION_EVENT, handleCustomEvent);
    window.removeEventListener("storage", handleStorageEvent);
  };
};

export default supportApi;
